<template>
  <div>
    <editable-map
      editable
      ref="theMap"
      id="theMap"
      :zoom.sync="currentZoom"
      :options="mapOptions"
      :center="center"
      :min-zoom="minZoom"
      :max-zoom="maxZoom"
      :bounds.sync="bounds"
      style="height: 100%; width: 100%"
      @ready="emitReady"
      @update:center="centerUpdated"
      @update:zoom="zoomUpdated"
      @update:bounds="boundsUpdated"
      @fullscreenchange="fullScreenChange"
    >
      <l-tile-layer
        v-for="tileProvider in tileProvidersLocal"
        :key="tileProvider.name"
        :name="tileProvider.name"
        :visible="tileProvider.visible"
        :url="tileProvider.url"
        :attribution="tileProvider.attribution"
        layer-type="base"
        :options="tileProvider.options"
        v-if="tileProvidersReady"
      />
      <l-control-fullscreen position="topleft"
        :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
      />
      <l-control-layers
        :position="layersPosition"
        collapsed
        sort-layers
      />
      <l-control-zoom :position="zoomPosition" />
      <l-control-attribution
        :position="attributionPosition"
        prefix="Moova"
      />
      <l-control-scale position="bottomright" :imperial="imperial" :metric="metric" />
      <l-control position="topright" >
        <div class="text-right">
          <b-button size="sm" @click="fitMap" v-translate>
            Fit map
          </b-button>
        </div>
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction" style="margin-top: 10px; box-shadow: none; height: 100%; max-height: calc(80vh - 180px); overflow: auto">
          <slot name="controls-topright"></slot>
        </div>
      </l-control>
      <l-control position="topleft" v-if="polygonable">
        <div class="leaflet-bar">
          <a href="#" @click.prevent="paths.length === 0 ? addPath() : removePath();" style="position: relative;">▱<span v-if="paths.length !== 0" style="position: absolute;left: 50%; transform: translateX(-50%);font-size: 1.5rem" class="text-danger">×</span></a>
        </div>
        <b-button size="sm" @click="multiRemoveFromCollection" :disabled="this.inPolygon.length === 0" style="padding: .2rem .4rem" v-if="polygonable" v-translate>Delete from Collection</b-button>
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction">
          <slot name="controls-topleft"></slot>
        </div>
      </l-control>
      <l-control position="topleft">
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction">
          <a class="leaflet-bar-part" :title="$gettext('Open view in Google Maps')" href="#" @click.prevent.stop="openInGoogleMaps" style="position: relative; left: 3px;">
            <img src="/static/img/gmaps.svg" alt="" style="width: 30px">
          </a>
          <all-in-one-search @center-updated="coordinatesFound" @bounds-updated="bounds = $event; $emit('showlocation', true);" />
          <slot name="controls-topleft"></slot>
        </div>
      </l-control>
      <l-control position="bottomleft">
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction">
          <slot name="controls-bottomleft"></slot>
        </div>
      </l-control>

      <!-- Map Layers -->
      <div v-if="mapLayers && Object.keys(mapLayers).length > 0">
        <l-layer-group :layer-type="mapLayers[type].layer?.type" :name="mapLayers[type].layer?.label" :ref="type" v-for="type in Object.keys(mapLayers)" :key="type + '_type'" v-if="mapLayers[type] !== null && typeof mapLayers[type] === 'object' && (mapLayers[type].markers || mapLayers[type].polygons)" :visible="mapLayers[type] && mapLayers[type].visible ? mapLayers[type].visible : false">
          <l-circle
          v-if="mapLayers[type]?.circles"
            v-for="(circle, index) in mapLayers[type].circles"
            :key="'circle' + index"
            :lat-lng="circle.center"
            :radius="circle.radius"
            :color="circle.color"
            :weight="circle.weight"
            :opacity="circle.opacity"
            :fillColor="circle.fillColor"
            :fillOpacity="circle.fillOpacity"
            :dashArray="circle.dashArray"
            :dashOffset="circle.dashOffset"
            :interactive="false"
            :options="{}"
            pane="overlayPane"
          />

          <l-marker-cluster :options="{spiderfyDistanceMultiplier: 1, maxClusterRadius: 30}" v-if="!withoutMarkerCluster">
            <map-marker
              v-if="typeof mapLayers[type] === 'object' && mapLayers[type].markers"
              v-for="(marker, i) in mapLayers[type].markers"
              :marker="marker"
              :key="'marker' + i"
              :ref="'marker' + marker.id"
              @marker-over="emitMarkerOver"
              @marker-out="emitMarkerOut"
              @delete-from-tour="emitDeleteFromTour"
              @item-removed="emitRemoveFromCollection"
              @move-to-cluster="emitMoveToCluster"
              @cluster-selected= "emitSelectCluster"
              @courier-selected="emitCourier"
              @close-popups="closePopups"
              @base-selected="emitBase"
              @data-selected="emitData"
              @create-shipping-from-item="emitCreateShippingFromItem"
            ></map-marker>
          </l-marker-cluster>
          <map-marker
            v-else-if="withoutMarkerCluster && typeof mapLayers[type] === 'object' && mapLayers[type].markers"
            v-for="(marker, i) in mapLayers[type].markers"
            :marker="marker"
            :key="'marker' + i"
            :ref="'marker' + marker.id"
            @marker-over="emitMarkerOver"
            @marker-out="emitMarkerOut"
            @delete-from-tour="emitDeleteFromTour"
            @item-removed="emitRemoveFromCollection"
            @move-to-cluster="emitMoveToCluster"
            @cluster-selected= "emitSelectCluster"
            @courier-selected="emitCourier"
            @close-popups="closePopups"
            @base-selected="emitBase"
            @data-selected="emitData"
            @create-shipping-from-item="emitCreateShippingFromItem"
          ></map-marker>
          <l-polyline
            v-if="typeof mapLayers[type] === 'object' && mapLayers[type].polylines"
            v-for="item in mapLayers[type].polylines"
            :key="item.id"
            :lat-lngs="item.points"
            :visible="item.visible || true"
            :color="item.strokeColor"
            :weight="item.strokeWeight"
          />

          <l-polygon
            v-for="(polygon, index) in mapLayers[type].polygons"
            :key="'polygon' + index"
            v-if="typeof mapLayers[type] === 'object' && mapLayers[type].polygons"
            :lat-lngs="polygon.coordinates"
            :color="stringToColour('11' + polygon.geo_type.name)"
            :fillColor="stringToColour('11' + polygon.geo_type.name)"
            :weight="2"
            :visible="mapLayers[type].visible"
          >
            <slot name="mapLayers-polygon-inside" v-bind:polygon="polygon">
              <l-tooltip>
                {{polygon.external_id | cleanentityprefix}} <strong>{{polygon.name | cleanentityprefix}} {{ polygon.reference_location ? `(${polygon.reference_location})` : ''}}</strong>
              </l-tooltip>
              <l-popup v-if="polygon.geo_type.level === 0" :options="{ permanent: false, sticky: false, closeButton: false }">
                <b-button size="sm" variant="dark" :to="{name: 'GeofencesEdit', params: {'id': polygon.id}}" target="_blank">
                  <font-awesome-icon icon="edit" />
                </b-button>
              </l-popup>
            </slot>
          </l-polygon>

        </l-layer-group>
      </div>

      <l-feature-group>
        <!-- stores markers -->
        <map-marker
          v-for="(marker, i) in stores"
          :marker="marker"
          :key="'store' + i"
          :ref="'marker' + marker.id"
          @marker-over="emitMarkerOver"
          @marker-out="emitMarkerOut"
          @delete-from-tour="emitDeleteFromTour"
          @item-removed="emitRemoveFromCollection"
          @move-to-cluster="emitMoveToCluster"
          @store-selected="emitStore"
          @base-selected="emitBase"
          @close-popups="closePopups"
        ></map-marker>

        <!-- couriers markers -->
        <map-marker
          v-for="(marker, i) in couriers"
          :marker="marker"
          :key="'courier' + i"
          :ref="'marker' + marker.id"
          @marker-over="emitMarkerOver"
          @marker-out="emitMarkerOut"
          @delete-from-tour="emitDeleteFromTour"
          @item-removed="emitRemoveFromCollection"
          @move-to-cluster="emitMoveToCluster"
          @courier-selected="emitCourier"
          @close-popups="closePopups"
        ></map-marker>

        <!-- generic markers & polylines -->
        <l-marker-cluster :options="{spiderfyDistanceMultiplier: 1, maxClusterRadius: 30}" v-if="!withoutMarkerCluster">
          <map-marker
            v-for="(marker, i) in markers"
            :marker="marker"
            :key="'marker' + i"
            :ref="'marker' + marker.id"
            @marker-over="emitMarkerOver"
            @marker-out="emitMarkerOut"
            @delete-from-tour="emitDeleteFromTour"
            @item-removed="emitRemoveFromCollection"
            @move-to-cluster="emitMoveToCluster"
            @cluster-selected= "emitSelectCluster"
            @close-popups="closePopups"
            @data-selected="emitData"
            @create-shipping-from-item="emitCreateShippingFromItem"
          ></map-marker>
        </l-marker-cluster>
        <map-marker
          v-else
          v-for="(marker, i) in markers"
          :marker="marker"
          :key="'marker' + i"
          :ref="'marker' + marker.id"
          @marker-over="emitMarkerOver"
          @marker-out="emitMarkerOut"
          @delete-from-tour="emitDeleteFromTour"
          @item-removed="emitRemoveFromCollection"
          @move-to-cluster="emitMoveToCluster"
          @cluster-selected= "emitSelectCluster"
          @close-popups="closePopups"
          @data-selected="emitData"
          @create-shipping-from-item="emitCreateShippingFromItem"
        ></map-marker>

        <!-- tracking markers & polylines -->
        <map-marker
          v-for="marker in tracking"
          :marker="marker"
          :key="marker.id"
          :ref="'marker' + marker.id"
          @marker-over="emitMarkerOver"
          @marker-out="emitMarkerOut"
          @close-popups="closePopups"
          :show-markers-text="showMarkersText"
        ></map-marker>

        <l-polyline
          v-for="item in polylines"
          :key="item.id"
          :lat-lngs="item.points"
          :visible="item.visible || true"
          :color="item.strokeColor"
          :weight="item.strokeWeight"
        />

        <l-polyline
          v-for="item in trackingPolylines"
          :key="item.id"
          :lat-lngs="item.points"
          :visible="item.visible || true"
          :color="item.strokeColor"
          :weight="item.strokeWeight"
          v-if="trackingPolylines.length > 0 && heathPath.length <= 0"
        />
        <l-hotline v-if="heathPath.length > 0" :latLngs="heathPath" :min="50" :max="0"></l-hotline>

        <editable-polygon
          ref="polygon"
          :editable="editablePolygon"
          :lat-lngs="paths"
          @editable:editing="updateEdited($event)"
        />
      </l-feature-group>

      <!-- Slot -->
      <slot name="map-data"></slot>
    </editable-map>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { featureGroup, Marker, LatLngBounds } from 'leaflet'
import {
  LMap,
  LTileLayer,
  LMarker,
  LPolyline,
  LPolygon,
  LCircle,
  LLayerGroup,
  LFeatureGroup,
  LTooltip,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { EditableMap, EditablePolygon } from 'vue2-leaflet-editable'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import LHotline from '@/components/Vue2LeafletHotline.vue'
import LMarkerCluster from 'vue2-leaflet-markercluster'
import LControlFullscreen from 'vue2-leaflet-fullscreen'
import MapMarker from '@/components/Maps/MapMarker'
import AllInOneSearch from '@/components/Maps/AllInOneSearch.vue'

export default {
  emits: ['assign-shipping', 'base-selected', 'bounds', 'bounds-updated', 'center-updated', 'cluster-selected', 'courier-selected', 'create-shipping-from-item', 'data-selected', 'delete-from-tour', 'fullscreenchange', 'item-removed', 'itemsRemoved', 'marker-out', 'marker-over', 'move-to-cluster', 'ready', 'showlocation', 'store-selected', 'zoom', 'zoom-updated'],
  name: 'LeafletMap',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LPolygon,
    LCircle,
    LLayerGroup,
    LFeatureGroup,
    LTooltip,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    EditableMap,
    EditablePolygon,
    LHotline,
    LMarkerCluster,
    LControlFullscreen,
    MapMarker,
    AllInOneSearch
  },
  data() {
    return {
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 11,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      metric: false,
      stores: [],
      couriers: [],
      markers: [],
      previousMarkers: null,
      polylines: [],
      paths: [],
      editablePolygon: false,
      inPolygon: [],
      bounds: null,
      fitted: false,
      shipping: {},
      courier: {},
      tracking: [],
      trackingPolylines: [],
      heathPath: [],
      tileProvidersReady: false,
      tileProvidersLocal: [],
      mapLayersHash: null
    }
  },
  props: {
    geolocate: {
      type: Boolean,
      default: false
    },
    fitOnce: {
      type: Boolean,
      default: false
    },
    fitAlways: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 11
    },
    centerMap: {
      type: Boolean,
      default: false
    },
    polygonable: {
      type: Boolean,
      default: false
    },
    mapLayers: {
      type: Object,
      default: () => {}
    },
    preMarkers: {
      type: Array,
      default: () => []
    },
    preCouriers: {
      type: Array,
      default: () => []
    },
    prePolylines: {
      type: Array,
      default: () => []
    },
    withoutMarkerCluster: {
      type: Boolean,
      default: false
    },
    defaultTileProvider: {
      type: String,
      default: null
    },
    showMarkersText: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    tileProviders: {
      handler(newVal, oldVal) {
        this.tileProvidersLocal = cloneDeep(newVal)
        console.log(this.tileProvidersLocal)
        this.setDefaultTileProvider()
      },
      deep: true
    },
    preMarkers: {
      handler(newVal, oldVal) {
        try {
          if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            this.markers = newVal
          }
        } catch (e) {
          this.markers = newVal
        }
      },
      deep: true
    },
    preCouriers: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.couriers = newVal
        }
      },
      deep: true
    },
    prePolylines: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.addPolylines(newVal)
        }
      },
      deep: true
    },
    mapLayers: {
      handler(newVal, oldVal) {
        if (!this.mapLayersHash) {
          this.mapLayersHash = JSON.stringify(newVal)
        }
        if (this.fitAlways && this.mapLayersHash !== JSON.stringify(oldVal)) {
          setTimeout(() => {
            this.fitMap()
            this.map.invalidateSize()
            this.mapLayersHash = JSON.stringify(newVal)
          }, 100)
        }
      },
      deep: true
    },
    markers(val) {
      this.clusters = [...new Set(val.map(x => { return x.infoText.cluster + 1 }).filter(Boolean))]
    },
    tracking(val) {
      if ((this.fitOnce && this.fitted === false) || this.fitAlways) {
        this.fitMap()
        this.map.invalidateSize()
      }
    },
    bounds(val) {
      this.$emit('bounds', val)
    },
    currentZoom(val) {
      console.log(val)
      this.$emit('zoom', val)
    }
  },
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial
    await this.checkMapProviders()
    this.center = this.frontConfig && this.frontConfig.maps && this.frontConfig.maps.general && this.frontConfig.maps.general.lat ? [this.frontConfig.maps.general.lat, this.frontConfig.maps.general.lng] : [0, 0]
    if (this.geolocate === true) {
      this.geolocateMap()
    }
    this.currentZoom = this.zoom
    this.polylines = this.prePolylines
    this.tileProvidersLocal = cloneDeep(this.tileProviders)
    setTimeout(() => {
      this.setDefaultTileProvider()
      this.fitMap()
      this.map.invalidateSize()
    }, 3000)
  },
  methods: {
    coordinatesFound($event) {
      this.currentZoom = 17
      setTimeout(() => {
        this.center = $event
      }, 500)
    },
    setDefaultTileProvider() {
      if (this.ecosystemMaps && this.ecosystemMaps !== null && this.ecosystemMaps.defaultTileProvider && this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
        this.tileProvidersLocal = this.tileProvidersLocal.map(x => {
          if (x.name === this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
      }
      this.tileProvidersReady = true
    },
    alert(item) {
      alert('this is ' + JSON.stringify(item))
    },
    geolocateMap() {
      navigator.geolocation.getCurrentPosition(position => {
        this.center = [position.coords.latitude, position.coords.longitude]
      })
    },
    addHeathPath(path) {
      this.heathPath = path
    },
    removeHeathPath() {
      this.heathPath = []
    },
    addTracking(items) {
      this.tracking = []
      this.tracking = items.map(x => this.transformMarker(x))
      const trackingPolylines = []
      if (items.length > 2) {
        trackingPolylines.push({
          polyLine: items,
          polyLineOptions: {
            strokeColor: '#f0f'
          }
        })
      }
      this.trackingPolylines = trackingPolylines.map((x, i) => {
        return {
          id: 't' + i,
          strokeWeight: x.polyLineOptions.strokeWeight || 1,
          strokeColor: x.polyLineOptions.strokeColor,
          points: x.polyLine.map(x => {
            x.lat = Number.parseFloat(x.lat)
            x.lng = Number.parseFloat(x.lng)
            return x
          })
        }
      })
    },
    addMarkers(items) {
      if (this.mapReady) {
        if (this.previousMarkers !== JSON.stringify(items)) {
          console.log('CHANGED!')
          this.polylines = []
          this.markers = []
          this.markers = items.map(x => this.transformMarker(x))
          this.previousMarkers = JSON.stringify(items)
          if ((this.fitOnce && this.fitted === false) || this.fitAlways) {
            this.fitMap()
            this.map.invalidateSize()
          }
        } else {
          console.log('NO CHANGES')
        }
      } else {
        this.addMarkers(items)
      }
    },
    addCouriers(items) {
      if (this.mapReady) {
        this.couriers = items.map(x => this.transformMarker(x))
      } else {
        this.addCouriers(items)
      }
    },
    addStores(items) {
      if (this.mapReady) {
        this.stores = items.map(x => this.transformMarker(x))
      } else {
        setTimeout(function () {
          this.addStores(items)
        }.bind(this, items), 150)
      }
    },
    addPolylines(items) {
      this.polylines = []
      this.polylines = items.map((x, i) => {
        x.id = i
        return this.transformPolyline(x)
      })
    },
    transformPolyline(item) {
      return {
        id: 'p' + item.id,
        strokeWeight: item.polyLineOptions.strokeWeight || 1,
        strokeColor: item.polyLineOptions.strokeColor,
        points: item.polyLine.filter(x => item.id !== 1000)
      }
    },
    addPairs(items) {
      const markers = []
      const polyLines = []

      for (const item of items) {
        markers.push(item.from)
        markers.push(item.to)

        if (item.polyLine === true) {
          polyLines.push({
            polyLine: [
              {
                lat: Number.parseFloat(item.from.lat),
                lng: Number.parseFloat(item.from.lng)
              },
              {
                lat: Number.parseFloat(item.to.lat),
                lng: Number.parseFloat(item.to.lng)
              }
            ],
            polyLineOptions: {
              strokeColor: '#FF6C87',
              strokeWeight: 1
            }
          })
        }
      }
      this.addMarkers(markers)
      this.addPolylines(polyLines)
    },
    transformMarker(item) {
      return {
        id: item.id,
        position: {
          lat: Number.parseFloat(item.lat),
          lng: Number.parseFloat(item.lng)
        },
        icon: typeof item.icon === 'string'
          ? {
              iconUrl: item.icon,
              iconAnchor: item.anchor || [item.x, item.y],
              iconSize: item.size || [item.x * 2, item.y * 2],
              tooltipAnchor: item.tooltipAnchor || [item.x, 0],
              popupAnchor: item.popupAnchor || [0, -(item.y / 2)]
            }
          : {
              iconType: item.icon.type,
              iconAnchor: item.icon.anchor,
              iconSize: item.icon.size,
              tooltipAnchor: item.icon.tooltipAnchor || [item.x, 0],
              popupAnchor: item.icon.popupAnchor || [0, -(item.y / 2)],
              iconColor: item.icon.color,
              iconText: item.icon.text,
              iconRotation: item.icon.direction ? (item.icon.direction <= 180 ? item.icon.direction : item.icon.direction - 360) : 0,
              iconDynamic: item.icon.dynamic || null,
              iconLoad: item.icon.load || null
            },
        infoText: item.infoText,
        tooltip: item.tooltip || null,
        popup: item.popup || null
      }
    },
    fitMap() {
      this.fitted = false
      if (this.markers.length === 1 && this.tracking.length === 0) {
        setTimeout(function () {
          this.map.invalidateSize()
          this.center = this.markers[0] ? this.markers[0].position : this.center
        }.bind(this), 100)
      } else {
        setTimeout(() => {
          let points = []
          if (this.mapLayers && Object.keys(this.mapLayers).length) {
            for (const key of Object.keys(this.mapLayers)) {
              if (this.mapLayers[key].visible) {
                points = points.concat(this.mapLayers[key].markers?.map(x => { return { lat: x.position.lat, lng: x.position.lng } })).filter(Boolean)
                points = points.concat(this.mapLayers[key].polygons?.map(x => { return x.fence.coordinates[0].map(([lng, lat]) => ({ lat, lng })) }))
              }
            }
          }

          const bounds = new LatLngBounds()
          if (points.length) {
            for (const latLng of points) {
              bounds.extend(latLng)
            }
          }

          if (bounds._southWest) {
            this.bounds = bounds
          } else {
            // eslint-disable-next-line
            let group = new featureGroup()

            this.$refs.theMap.mapObject.eachLayer((layer) => {
              if (layer._bounds !== undefined && !layer._bounds.max) {
                group.addLayer(layer)
              }
              if (this.markers.length > 1 || this.tracking.length > 1) {
                const markersAndTracking = this.markers.concat(this.tracking).map(o => o.position)
                for (const marker of markersAndTracking) {
                  const markerToAdd = new Marker(marker)
                  markerToAdd.addTo(group)
                }
              }
            })

            try {
              const bounds = group.getBounds()
              if (bounds._southWest) {
                this.$refs.theMap.fitBounds(bounds, { padding: [200, 200] })
              }
            } catch (e) {}
          }
          this.fitted = true
        }, 500)
      }
    },
    addPath () {
      this.noFreehand = true
      // obtain the bounds, so we can guess how big the polygon should be
      const bounds = this.map.getBounds()
      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()
      const center = bounds.getCenter()
      const degree = this.paths.length + 1
      const f = Math.pow(0.66, degree)

      // Draw a triangle. Use f to control the size of the triangle.
      // i.e., every time we add a path, we reduce the size of the triangle
      const path = [
        { lng: center.lng, lat: (1 - f) * center.lat + (f) * northEast.lat },
        { lng: (1 - f) * center.lng + (f) * southWest.lng, lat: (1 - f) * center.lat + (f) * southWest.lat },
        { lng: (1 - f) * center.lng + (f) * northEast.lng, lat: (1 - f) * center.lat + (f) * southWest.lat }
      ]

      this.paths.push(path)
      this.$nextTick(() => {
        this.editablePolygon = true
      })
      this.updateInPolygon()
    },
    removePath() {
      this.editablePolygon = false
      this.paths = []
      this.inPolygon = []
    },
    updateInPolygon() {
      this.inPolygon = []
      const polys = {
        type: 'Polygon',
        coordinates: this.paths.map(path => this.closeLoop(path.map(({ lat, lng }) => [lng, lat])))
      }

      this.markers.forEach((marker, i) => {
        const point = {
          type: 'Point',
          coordinates: [marker.position.lng, marker.position.lat]
        }
        if (booleanPointInPolygon(point, polys)) {
          this.inPolygon.push(this.markers[i].infoText.id)
        }
      })
    },
    updateEdited(e) {
      const latLngs = e.layer.getLatLngs()[0]
      this.paths[0] = latLngs.map(ll => { return { lat: ll.lat, lng: ll.lng } })
      this.updateInPolygon()
    },
    closeLoop (path) {
      return path.concat(path.slice(0, 1))
    },
    multiRemoveFromCollection() {
      this.$emit('itemsRemoved', this.inPolygon)
      this.removePath()
    },
    disableMapInteraction() {
      this.map.dragging.disable()
      this.map.touchZoom.disable()
      this.map.doubleClickZoom.disable()
      this.map.scrollWheelZoom.disable()
      this.map.boxZoom.disable()
      this.map.keyboard.disable()
      if (this.map.tap) this.map.tap.disable()
    },
    enableMapInteraction() {
      this.map.dragging.enable()
      this.map.touchZoom.enable()
      this.map.doubleClickZoom.enable()
      this.map.scrollWheelZoom.enable()
      this.map.boxZoom.enable()
      this.map.keyboard.enable()
      if (this.map.tap) this.map.tap.enable()
    },
    closePopups() {
      this.map.closePopup()
    },
    emitReady() {
      this.map = this.$refs.theMap.mapObject
      this.map.invalidateSize()
      // this.map.on('popupclose', () => this.emitCourier(null))
      this.mapReady = true
      this.$emit('ready', true)
      this.setDefaultTileProvider()
      console.log(this.mapLayers)
    },
    emitCourier(courier) {
      this.$emit('courier-selected', courier)
    },
    emitBase(base) {
      this.$emit('base-selected', base)
    },
    emitStore(store) {
      this.$emit('store-selected', store)
    },
    emitData(data) {
      this.$emit('data-selected', data)
    },
    emitRemoveFromCollection(i) {
      this.$emit('item-removed', i)
    },
    emitSelectCluster(i) {
      this.$emit('cluster-selected', i)
    },
    emitMoveToCluster(data) {
      this.$emit('move-to-cluster', data)
    },
    emitAssignShipping(courierId = null, shippingId = null) {
      shippingId = shippingId || this.shipping.id
      courierId = courierId || this.courier.id
      this.$emit('assign-shipping', { courierId, shippingId })
      this.shipping = {}
      this.courier = {}
      this.infoWinOpen = false
    },
    emitMarkerOver(marker, e = null) {
      this.fitted = true
      this.$emit('marker-over', marker, e)
    },
    emitMarkerOut(marker, e = null) {
      this.fitted = true
      this.$emit('marker-out', marker, e)
    },
    emitDeleteFromTour(id) {
      this.$emit('delete-from-tour', id)
    },
    emitCreateShippingFromItem(item) {
      this.$emit('create-shipping-from-item', item)
    },
    zoomUpdated(zoom) {
      this.currentZoom = zoom
      this.$emit('zoom-updated', zoom)
    },
    centerUpdated(center) {
      this.center = [center.lat, center.lng]
      this.$emit('center-updated', this.center)
    },
    boundsUpdated(bounds) {
      this.bounds = bounds
      this.$emit('bounds-updated', bounds)
    },
    fullScreenChange(e) {
      this.$emit('fullscreenchange', e)
    },
    openInGoogleMaps() {
      window.open(`https://www.google.com/maps/@${this.center.join(',')},${this.currentZoom}z`)
    }
  }
}
</script>

<style lang="scss">
.leaflet-container {
  font-family: inherit !important;
}
.leaflet-popup-content-wrapper {
  border-radius: 8px !important;
}
.leaflet-popup-content {
  font-size: .9em;
  hr {
    margin-top: .5em !important;
    margin-bottom: .5em !important;
  }
  h3, h5, h6, p {
    margin: 0 0 .3em !important;
  }
}
.pin-text {
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
}
.marker-circle {
  border-radius: 100px;
  border: 1px solid #000;
  background: #fff;
  text-align: center;
  span {
    font-size: 8px;
    line-height: 8px;
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    text-shadow: 0px 0px 2px rgba(255, 255, 255, 1);
  }
}
.leaflet-control-layers label {
  display: block !important;
}
.leaflet-top {
  z-index: 1001 !important;
}
.leaflet-control-geosearch .results.active {
  max-height: 250px;
  overflow-y: auto;
}
</style>
